import { SETTINGS } from "../settings";

/**
 * Fallback copy, used until the backend adds `ui.errors.geolocationDenied` to the
 * config response. `total.maps.L10n` is populated remotely (main.js), not from a
 * local file, so new strings cannot ship from the front-end alone.
 */
const FALLBACK = {
    fr: "Localisation refusée. Autorisez l'accès dans les réglages de votre navigateur.",
    en: "Location denied. Allow access in your browser settings.",
};

const NOTICE_ID = "geoloc-notice";

/**
 * Auto-dismiss delay. Requested by QA; 8s rather than the "few seconds" asked for because the
 * longer (French) string is 11 words, which needs ~6s at the reading rate assumed for timed
 * content, plus the beat it takes to notice the bar appeared at all.
 *
 * Auto-dismissing an error message engages WCAG 2.2.1 (Timing Adjustable), so the timer pauses
 * while the pointer is over the notice or focus is inside it — without that, this would be an
 * unadjustable time limit on the only explanation the user gets.
 */
const AUTO_DISMISS_MS = 8000;

let dismissTimer = null;

function stopDismissTimer() {
    if (dismissTimer !== null) {
        clearTimeout(dismissTimer);
        dismissTimer = null;
    }
}

function startDismissTimer() {
    stopDismissTimer();
    dismissTimer = setTimeout(hideGeolocationNotice, AUTO_DISMISS_MS);
}

/**
 * Restart the countdown once the user is no longer reading. Restarts rather than resumes the
 * remainder — the generous reading of "how long has this been on screen" is the accessible one.
 */
function resumeDismissTimer() {
    const notice = getNotice();
    if (!notice) return;

    // Removing the node while the close button holds focus would drop focus to <body>
    // (WCAG 2.4.3), so a keyboard user parked on it keeps the notice indefinitely.
    if (notice.contains(document.activeElement)) return;
    if (notice.matches(":hover")) return;

    startDismissTimer();
}

function resolveMessage() {
    const remote = total.maps.L10n?.ui?.errors?.geolocationDenied;
    if (remote) return remote;

    const lang = (SETTINGS.language || "en").slice(0, 2).toLowerCase();
    return FALLBACK[lang] || FALLBACK.en;
}

function getNotice() {
    return document.querySelector(`[data-id="${NOTICE_ID}"]`);
}

/**
 * Built here rather than in main.htm because SearchView replaces the sidebar's
 * innerHTML on init (searchView.js), which would discard a template-authored node.
 *
 * `role="alert"` rather than `role="status"`: this node is created at the moment the
 * message appears, and a live region inserted together with its content is routinely
 * missed by screen readers. An alert is specified to announce on insertion, and an
 * error caused by the user pressing a button is a legitimate assertive announcement.
 */
function createNotice(message) {
    const notice = document.createElement("div");
    notice.className = "wm-notice";
    notice.setAttribute("data-id", NOTICE_ID);
    notice.setAttribute("role", "alert");

    const text = document.createElement("span");
    text.className = "wm-notice--text";
    text.textContent = message;
    notice.appendChild(text);

    const close = document.createElement("button");
    close.type = "button";
    close.className = "wm-notice--close";
    close.setAttribute("data-id", `${NOTICE_ID}-close`);
    close.setAttribute("aria-label", total.maps.L10n?.ui?.common?.close || "Close");
    close.textContent = "×";
    close.addEventListener("click", hideGeolocationNotice);
    notice.appendChild(close);

    // Listeners live on the node itself, so they go away with it — nothing to unbind on remove.
    notice.addEventListener("mouseenter", stopDismissTimer);
    notice.addEventListener("mouseleave", resumeDismissTimer);
    notice.addEventListener("focusin", stopDismissTimer);
    notice.addEventListener("focusout", (event) => {
        // relatedTarget is the element gaining focus; ignore moves within the notice.
        if (!notice.contains(event.relatedTarget)) resumeDismissTimer();
    });

    return notice;
}

/**
 * Show the "location denied" notice, directly under the sidebar's active card.
 * Call only after a user-initiated geolocation request fails with PERMISSION_DENIED —
 * never on load, and never for a timeout or an unavailable position.
 */
export function showGeolocationNotice() {
    const sidebar = document.querySelector(".wm-panel--sidebar");
    if (!sidebar) return;

    // Always a fresh node: the sidebar is re-rendered on view changes (which would
    // discard a reused one), and re-inserting the alert is what triggers the
    // announcement — mutating an identical message in place would not.
    hideGeolocationNotice();
    sidebar.appendChild(createNotice(resolveMessage()));
    startDismissTimer();
}

/**
 * Hide the notice. Called on the next geolocation attempt, on search input, when
 * leaving route or directions mode, from the close button, and by the auto-dismiss timer.
 *
 * Always clears the timer: otherwise one left over from a dismissed notice would fire later
 * and remove whichever notice happened to be on screen by then.
 */
export function hideGeolocationNotice() {
    stopDismissTimer();
    getNotice()?.remove();
}
